/**
 * Places on a map (owner remark 3/12) and the distance from the warehouse (for transport prices, P5). Pure: used by
 * the server and by the browser (`@app/shared/geo`), so both show the same number.
 */
export type GeoPoint = { lat: number; lng: number };

const EARTH_RADIUS_KM = 6371;
const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in km. */
export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** How far (km) a point may be from a place and still be that place (its entrance, its car park): review I4. */
export const PLACE_ID_RADIUS_KM = 0.3;

/** Whether two points are the same place (within `PLACE_ID_RADIUS_KM`). */
export const samePlace = (a: GeoPoint, b: GeoPoint): boolean => haversineKm(a, b) <= PLACE_ID_RADIUS_KM;

/** Valid coordinates: latitude −90…90, longitude −180…180. */
export const isGeoPoint = (v: unknown): v is GeoPoint => {
  const p = v as GeoPoint;
  return !!p && typeof p === 'object' && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;
};

/** "Otwórz w Google Maps": the coordinates when known, else the address; null when there is neither. With the Google place id its card. */
export function googleMapsUrl(place: { point: GeoPoint | null; address: string; placeId?: string | null }): string | null {
  const query = place.point ? `${place.point.lat},${place.point.lng}` : place.address.trim();
  if (query === '') return null;
  // with the place id Google opens the place's card (name, photos), not a bare pin
  const id = place.placeId ? `&query_place_id=${encodeURIComponent(place.placeId)}` : '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}${id}`;
}

/** "Otwórz trasę w Google Maps": by car from the warehouse to the place, to read the km of the chosen route; null without either. */
export function googleMapsRouteUrl(from: GeoPoint | null | undefined, to: GeoPoint | null | undefined): string | null {
  if (!from || !to) return null;
  const q = new URLSearchParams({ api: '1', origin: `${from.lat},${from.lng}`, destination: `${to.lat},${to.lng}`, travelmode: 'driving' });
  return `https://www.google.com/maps/dir/?${q}`;
}

/**
 * "Nawiguj" (owner 2026-10-21 22:54: "nie dawaj miejsc pośrednich"): Google Maps directions to the place only — no
 * origin, so the phone starts where it is and goes straight into turn-by-turn navigation; no waypoints. The point when
 * known, else the address; the Google place id with it when there is one. Null without a point and an address.
 */
export function navigateUrl(place: { point: GeoPoint | null; address: string; placeId?: string | null; placePoint?: GeoPoint | null }): string | null {
  const destination = place.point ? `${place.point.lat},${place.point.lng}` : place.address.trim();
  if (destination === '') return null;
  const q = new URLSearchParams({ api: '1', destination });
  // a place id the point has left would lead to the old place (review I4)
  const stale = !!place.point && !!place.placePoint && !samePlace(place.point, place.placePoint);
  if (place.placeId && !stale) q.set('destination_place_id', place.placeId);
  q.set('travelmode', 'driving');
  q.set('dir_action', 'navigate');
  return `https://www.google.com/maps/dir/?${q}`;
}

/**
 * The points of an encoded polyline (Google's format: what the Routes API sends as `encodedPolyline`), for drawing a
 * route on the map. A broken tail is dropped: only whole points are given.
 */
export function decodePolyline(encoded: string): GeoPoint[] {
  const points: GeoPoint[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  /** One signed number, or null when the text ends in the middle of it. */
  const next = (): number | null => {
    let result = 0;
    let shift = 0;
    for (;;) {
      if (index >= encoded.length) return null;
      const b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
      if (b < 0x20) break;
    }
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (index < encoded.length) {
    const dLat = next();
    const dLng = next();
    if (dLat === null || dLng === null) break;
    lat += dLat;
    lng += dLng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}
